import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { X, Plus, ExternalLink, Sparkles, Paperclip, Trash2, RotateCcw, Edit, ChevronDown, ChevronRight, FileText } from 'lucide-react';
import { purchasesAPI, suppliersAPI, purchasePaymentsAPI, aiPurchasesAPI, settingsAPI } from '../../api';
import SupplierPicker from '../../components/SupplierPicker';
import Layout from '../../components/Layout';
import SlideOver from '../../components/ui/SlideOver';
import { useToast } from '../../hooks/useToast';
import { SORTED_CATEGORIES, getCategoryName, getCategoryCode } from '../../constants/accountingCategories';
import { PURCHASE_DESTINATION_TYPES } from '../../lib/assetConstants';
import { Button } from '../../components/ui/Button';
import { RotatingGlowButton } from '../../components/ui/RotatingGlowButton';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { Card, CardContent } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { FormLabel } from '../../components/ui/FormLabel';
import { PurchasePaymentForm } from '../../components/purchases/PurchasePaymentForm';
import { formatCurrency, toPence, fromPence, calculateVatFromNetPence } from '../../lib/utils';

const PurchaseEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const viewReadOnly = searchParams.get('view') === 'readonly';
  const isEditMode = !!id;
  const { success, error: toastError, ToastContainer } = useToast();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [purchaseStatus, setPurchaseStatus] = useState(null);

  // Purchase form state
  const [formData, setFormData] = useState({
    supplierId: '',
    purchaseNum: '',
    receiptNumber: '',
    supplierRef: '',
    purchaseDate: '',
    deliveryDate: '',
    vatRate: 20,
    notes: '',
  });

  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [showSupplierPicker, setShowSupplierPicker] = useState(false);
  const [showNewSupplierSlideOver, setShowNewSupplierSlideOver] = useState(false);
  const [items, setItems] = useState([]);
  const [payments, setPayments] = useState([]);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [purchaseTotals, setPurchaseTotals] = useState({ total: 0, amountPaid: 0, balanceDue: 0 });
  const [showVatCalc, setShowVatCalc] = useState(false);
  const [vatCalcValues, setVatCalcValues] = useState({
    unitPriceIncVat: '',
    exVat: '',
    vat: '',
  });
  const [deliveryDateNotApplicable, setDeliveryDateNotApplicable] = useState(false);
  const [aiDraftId, setAiDraftId] = useState(null);
  const [aiSourceFileName, setAiSourceFileName] = useState(null);
  const [openingDocument, setOpeningDocument] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const fileInputRef = useRef(null);
  const [aiUploadEnabled, setAiUploadEnabled] = useState(false);
  const autoSaveEnabledRef = useRef(false);
  const vatRateInputRef = useRef(null);
  const [tenantSettings, setTenantSettings] = useState(null);
  const [docExpandAttachments, setDocExpandAttachments] = useState(true);
  const [docExpandNotes, setDocExpandNotes] = useState(false);

  useEffect(() => {
    if (isEditMode) {
      autoSaveEnabledRef.current = false;
      fetchPurchase();
    } else {
      // Generate next purchase number
      const timestamp = Date.now().toString().slice(-6);
      setFormData(prev => ({
        ...prev,
        purchaseNum: `PUR-${timestamp}`,
      }));
    }
  }, [id]);

  // Fetch tenant settings for document-style read-only view
  useEffect(() => {
    if (viewReadOnly && isEditMode) {
      settingsAPI.get()
        .then((res) => setTenantSettings(res.settings || {}))
        .catch(() => setTenantSettings({}));
    }
  }, [viewReadOnly, isEditMode]);

  // Check if AI upload feature is enabled
  useEffect(() => {
    const checkAIFeature = async () => {
      try {
        const response = await settingsAPI.get();
        const featureFlags = response.settings?.feature_flags || {};
        setAiUploadEnabled(featureFlags.ai_purchases_enabled === true);
      } catch (err) {
        console.error('Error checking AI feature flag:', err);
        setAiUploadEnabled(false);
      }
    };
    checkAIFeature();
  }, []);

  // Helper: effective VAT rate for an item (per-item override or purchase default)
  const getEffectiveVatRate = (item) => item.vat_rate ?? formData.vatRate ?? 0;

  // Recalculate line items when VAT rate changes (only items without per-item override)
  useEffect(() => {
    if (items.length > 0) {
      const updatedItems = items.map(item => {
        if (item.vat_rate != null) return item;
        const quantity = parseFloat(item.quantity) || 0;
        const unitPrice = parseFloat(item.unitPrice) || 0;
        const net = quantity * unitPrice;
        const vatRate = formData.vatRate || 0;
        const vat = (net * vatRate) / 100;
        const total = net + vat;
        return {
          ...item,
          lineTotal: net,
          lineVat: vat,
          lineTotalWithVat: total,
        };
      });
      setItems(updatedItems);
    }
  }, [formData.vatRate]);

  // Debounced auto-save when form data or items change (edit mode only)
  useEffect(() => {
    if (!isEditMode || !id || !autoSaveEnabledRef.current || purchaseStatus === 'cancelled' || viewReadOnly) return;
    if (!formData.supplierId || !formData.purchaseNum || items.length === 0) return;
    const invalidItems = items.filter(item => !item.description || !item.description.trim());
    if (invalidItems.length > 0) return;

    const purchaseData = {
      supplier_id: formData.supplierId,
      purchase_num: formData.purchaseNum,
      receipt_number: formData.receiptNumber || null,
      supplier_ref: formData.supplierRef || null,
      purchase_date: formData.purchaseDate || null,
      ...(deliveryDateNotApplicable ? {} : { delivery_date: formData.deliveryDate || null }),
      vat_rate: formData.vatRate,
      notes: formData.notes,
      items: items.map(item => ({
        description: item.description.trim(),
        part_number: item.partNumber || null,
        quantity: parseFloat(item.quantity) || 0,
        unit_price: parseFloat(item.unitPrice) || 0,
        category: item.category || null,
        notes: item.notes || null,
        vat_rate: item.vat_rate ?? null,
        destination_type: item.destinationType || 'expense',
        linked_asset_id: item.linkedAssetId || null,
        linked_stock_item_id: item.linkedStockItemId || null,
      })),
    };

    const timeoutId = setTimeout(async () => {
      try {
        const res = await purchasesAPI.update(id, purchaseData);
        const p = res.purchase || res;
        setPurchaseTotals({
          total: parseFloat(p.total) ?? 0,
          amountPaid: parseFloat(p.amount_paid) ?? parseFloat(p.amountPaid) ?? 0,
          balanceDue: parseFloat(p.balance_due) ?? parseFloat(p.balanceDue) ?? 0,
        });
        if (Array.isArray(p.items) && p.items.length > 0) {
          setItems((prev) =>
            prev.map((item, idx) => {
              const saved = p.items[idx];
              if (!saved) return item;
              return {
                ...item,
                id: saved.id,
                destinationType: saved.destinationType || item.destinationType,
                linkedAssetId: saved.linkedAssetId ?? item.linkedAssetId,
                linkedStockItemId: saved.linkedStockItemId ?? item.linkedStockItemId,
              };
            })
          );
        }
        success('Saved', 'Purchase updated.');
      } catch (err) {
        const msg = err.response?.data?.error || err.message || 'Failed to save';
        toastError('Save failed', msg);
        console.error('Purchase auto-save error:', err);
      }
    }, 800);
    return () => clearTimeout(timeoutId);
  }, [formData, items, deliveryDateNotApplicable, isEditMode, id, purchaseStatus, viewReadOnly, success, toastError]);

  // Helper to normalize category: if it's a name, convert to code; if it's already a code, keep it
  const normalizeCategory = (category) => {
    if (!category) return '';
    // Check if it's already a code (matches one of our codes)
    const isCode = SORTED_CATEGORIES.some(cat => cat.code === category);
    if (isCode) return category;
    // Otherwise, try to find the code by name
    return getCategoryCode(category) || category;
  };

  const fetchPurchase = async () => {
    try {
      setLoading(true);
      setError('');
      const purchase = await purchasesAPI.getOne(id);

      const rawVatRate = purchase.vatRate;
      const mappedVatRate = rawVatRate === null || rawVatRate === undefined || Number.isNaN(parseFloat(rawVatRate))
        ? 20
        : parseFloat(rawVatRate);

      setFormData({
        supplierId: purchase.supplier.id,
        purchaseNum: purchase.purchaseNum,
        receiptNumber: purchase.receiptNumber || '',
        supplierRef: purchase.supplierRef || '',
        purchaseDate: purchase.purchaseDate || '',
        deliveryDate: purchase.deliveryDate || '',
        vatRate: mappedVatRate,
        notes: purchase.notes || '',
      });

      // Set delivery date "not applicable" if delivery date is null/empty
      setDeliveryDateNotApplicable(!purchase.deliveryDate);

      setPurchaseStatus(purchase.status);
      setSelectedSupplier(purchase.supplier);
      setAiDraftId(purchase.aiDraftId ?? null);
      setAiSourceFileName(purchase.aiSourceFileName ?? null);
      // Normalize categories when loading (convert names to codes for backward compatibility)
      // Also calculate line totals, VAT, and total with VAT
      const vatRate = mappedVatRate;
      const normalizedItems = (purchase.items || []).map(item => {
        const quantity = parseFloat(item.quantity) || 0;
        const unitPrice = parseFloat(item.unitPrice) || 0;
        const net = quantity * unitPrice;
        const itemVatRate = item.vat_rate ?? item.vatRate ?? null;
        const effectiveRate = itemVatRate ?? vatRate;
        const vat = (net * effectiveRate) / 100;
        const total = net + vat;
        return {
          ...item,
          destinationType: item.destinationType || 'expense',
          linkedAssetId: item.linkedAssetId || null,
          linkedStockItemId: item.linkedStockItemId || null,
          vat_rate: itemVatRate,
          category: normalizeCategory(item.category),
          lineTotal: net,
          lineVat: vat,
          lineTotalWithVat: total,
        };
      });
      setItems(normalizedItems);
      setPurchaseTotals({
        total: purchase.total || 0,
        amountPaid: purchase.amountPaid || 0,
        balanceDue: purchase.balanceDue || 0,
      });
      // Enable auto-save after a short delay so we don't save immediately after load
      setTimeout(() => { autoSaveEnabledRef.current = true; }, 500);

      // Fetch payments and attachments
      await Promise.all([fetchPayments(), fetchAttachments()]);
    } catch (err) {
      setError(err.message || 'Failed to load purchase');
      console.error('Error fetching purchase:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttachments = async () => {
    if (!id) return;
    try {
      const res = await purchasesAPI.getAttachments(id);
      setAttachments(res.attachments || []);
    } catch (err) {
      console.error('Error fetching attachments:', err);
      setAttachments([]);
    }
  };

  const fetchPayments = async () => {
    if (!id) return;
    try {
      const response = await purchasePaymentsAPI.getAll({ purchase_id: id });
      setPayments(response.payments || []);
    } catch (err) {
      console.error('Error fetching payments:', err);
    }
  };

  const handlePaymentRecorded = () => {
    fetchPayments();
    fetchPurchase(); // Refresh purchase to get updated totals
    setShowPaymentForm(false); // Close the payment form slide-over
    // Show success notification
    success(
      'Payment Recorded',
      'The payment has been successfully recorded and the purchase totals have been updated.'
    );
  };

  const handleSupplierSelect = (supplier) => {
    setSelectedSupplier(supplier);
    setFormData(prev => ({ ...prev, supplierId: supplier.id }));
    setShowSupplierPicker(false);
  };

  const handleNewSupplierCreated = (supplier) => {
    // Select the newly created supplier
    handleSupplierSelect(supplier);
    setShowNewSupplierSlideOver(false);
  };

  const handleViewOriginalDocument = async () => {
    if (!aiDraftId && !id) return;
    try {
      setOpeningDocument(true);
      let blob;
      try {
        blob = aiDraftId
          ? await aiPurchasesAPI.getDraftFile(aiDraftId)
          : await purchasesAPI.getPrimaryAttachmentFile(id);
      } catch (firstErr) {
        // Fallback: if draft file returns 404, try purchase primary attachment (same file, different endpoint)
        if (firstErr.response?.status === 404 && id) {
          blob = await purchasesAPI.getPrimaryAttachmentFile(id);
        } else {
          throw firstErr;
        }
      }
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke after a delay so the new tab can load; browser cleans up when tab closes
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      console.error('Error opening original document:', err);
      toastError(
        'Could not open document',
        err.response?.status === 403
          ? 'AI purchases may be disabled for your account.'
          : (err.message || 'Failed to load the original uploaded file.')
      );
    } finally {
      setOpeningDocument(false);
    }
  };

  const handleViewAttachment = async (attachmentId) => {
    if (!id) return;
    try {
      setOpeningDocument(true);
      const blob = await purchasesAPI.getAttachmentFile(id, attachmentId);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      console.error('Error opening attachment:', err);
      toastError('Could not open document', err.message || 'Failed to load the file.');
    } finally {
      setOpeningDocument(false);
    }
  };

  const handleUploadAttachment = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !id) return;
    const allowed = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.gif', '.webp'];
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!allowed.includes(ext)) {
      toastError('Invalid file type', 'Only PDF, DOC, DOCX and image files are allowed.');
      return;
    }
    try {
      setUploadingAttachment(true);
      const res = await purchasesAPI.uploadAttachment(id, file);
      setAttachments(prev => [res.attachment, ...prev]);
      success('Attachment uploaded', 'Receipt saved successfully.');
    } catch (err) {
      toastError('Upload failed', err.response?.data?.error || err.message || 'Failed to upload file.');
    } finally {
      setUploadingAttachment(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!id || !confirm('Delete this attachment?')) return;
    try {
      await purchasesAPI.deleteAttachment(id, attachmentId);
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
      success('Attachment deleted', 'Receipt removed.');
    } catch (err) {
      toastError('Delete failed', err.response?.data?.error || err.message || 'Failed to delete attachment.');
    }
  };

  const handleAddItem = (insertAfterIndex = null) => {
    const quantity = 1;
    const unitPrice = 0;
    const net = quantity * unitPrice;
    const vatRate = formData.vatRate || 0;
    const vat = (net * vatRate) / 100;
    const total = net + vat;
    const newItem = {
      id: `temp-${Date.now()}`,
      description: '',
      partNumber: '',
      quantity: 1,
      unitPrice: 0,
      category: '5000', // Default to 'General Purchases'
      notes: '',
      vat_rate: null,
      lineTotal: net,
      lineVat: vat,
      lineTotalWithVat: total,
      destinationType: 'expense',
      linkedAssetId: null,
      linkedStockItemId: null,
    };
    
    if (insertAfterIndex !== null && insertAfterIndex >= 0) {
      // Insert after the specified index
      const newItems = [...items];
      newItems.splice(insertAfterIndex + 1, 0, newItem);
      setItems(newItems);
    } else {
      // Append to the end
      setItems([...items, newItem]);
    }
  };

  const handleUpdateItem = (index, field, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };

    if (field === 'vatAmountOverride') {
      const amount = parseFloat(String(value).replace(/[^0-9.-]/g, '')) || 0;
      const quantity = parseFloat(updated[index].quantity) || 0;
      const unitPrice = parseFloat(updated[index].unitPrice) || 0;
      const net = quantity * unitPrice;
      let vatRate;
      if (amount === 0) {
        vatRate = 0;
      } else if (net !== 0) {
        vatRate = Math.round(100 * 100 * amount / net) / 100;
      } else {
        vatRate = 0;
      }
      updated[index].vat_rate = vatRate;
      const netPence = Math.round(net * 100);
      const vatPence = calculateVatFromNetPence(netPence, vatRate);
      updated[index].lineTotal = net;
      updated[index].lineVat = fromPence(vatPence);
      updated[index].lineTotalWithVat = net + fromPence(vatPence);
    } else if (field === 'vat_rate' && value === null) {
      // Clear override: recalculate using purchase default rate
      const quantity = parseFloat(updated[index].quantity) || 0;
      const unitPrice = parseFloat(updated[index].unitPrice) || 0;
      const vatRate = formData.vatRate || 0;
      const { vatPence, totalPence } = calculateLinePence(quantity, unitPrice, vatRate);
      const net = quantity * unitPrice;
      updated[index].lineTotal = net;
      updated[index].lineVat = fromPence(vatPence);
      updated[index].lineTotalWithVat = fromPence(totalPence);
    } else if (field === 'quantity' || field === 'unitPrice') {
      const quantity = parseFloat(updated[index].quantity) || 0;
      const unitPrice = parseFloat(updated[index].unitPrice) || 0;
      const net = quantity * unitPrice;
      const vatRate = getEffectiveVatRate(updated[index]);
      const { vatPence, totalPence } = calculateLinePence(quantity, unitPrice, vatRate);
      updated[index].lineTotal = net;
      updated[index].lineVat = fromPence(vatPence);
      updated[index].lineTotalWithVat = fromPence(totalPence);
    }

    setItems(updated);
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleVatCalcChange = (rawValue) => {
    const rate = formData.vatRate || 0;

    if (rawValue === '') {
      setVatCalcValues({ unitPriceIncVat: '', exVat: '', vat: '' });
      return;
    }

    const value = parseFloat(rawValue);
    if (Number.isNaN(value)) {
      // Non-numeric input, just echo back without calculating
      setVatCalcValues(prev => ({ ...prev, unitPriceIncVat: rawValue }));
      return;
    }

    let exVat = value;
    let vat = 0;

    if (rate > 0) {
      exVat = value / (1 + rate / 100);
      vat = value - exVat;
    }

    setVatCalcValues({
      unitPriceIncVat: rawValue,
      exVat: exVat.toFixed(2),
      vat: vat.toFixed(2),
    });
  };

  // Helper: calculate a line's net/VAT/total using integer pence to avoid rounding drift
  const calculateLinePence = (quantity, unitPrice, vatRate) => {
    const qty = Number.isFinite(quantity) ? quantity : 0;
    const price = Number.isFinite(unitPrice) ? unitPrice : 0;
    const rate = Number.isFinite(vatRate) ? vatRate : 0;

    const netPence = Math.round(qty * price * 100);
    const vatPence = calculateVatFromNetPence(netPence, rate);
    const totalPence = netPence + vatPence;

    return { netPence, vatPence, totalPence };
  };

  const calculateTotals = () => {
    let subtotalPence = 0;
    let vatPence = 0;

    items.forEach((item) => {
      const quantity = parseFloat(item.quantity) || 0;
      const unitPrice = parseFloat(item.unitPrice) || 0;
      const effectiveRate = getEffectiveVatRate(item);
      const { netPence, vatPence: lineVatPence } = calculateLinePence(quantity, unitPrice, effectiveRate);
      subtotalPence += netPence;
      vatPence += lineVatPence;
    });

    const totalPence = subtotalPence + vatPence;

    return {
      subtotalPence,
      vatPence,
      totalPence,
      subtotal: fromPence(subtotalPence),
      vatAmount: fromPence(vatPence),
      total: fromPence(totalPence),
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isReadOnly) return;

    if (!formData.supplierId) {
      setError('Please select a supplier');
      return;
    }

    if (!formData.purchaseNum) {
      setError('Purchase number is required');
      return;
    }

    if (items.length === 0) {
      setError('Please add at least one purchase item');
      return;
    }

    // Validate items before submission
    const invalidItems = items.filter(item => !item.description || !item.description.trim());
    if (invalidItems.length > 0) {
      setError('Please ensure all items have a description');
      return;
    }

    // Don't allow editing cancelled purchases
    if (purchaseStatus === 'cancelled') {
      setError('Cannot edit a cancelled purchase');
      return;
    }

    try {
      setSaving(true);
      setError('');

      // Read VAT rate from the input ref so we use the current DOM value on submit.
      // This avoids the value reverting to 20% when the user changes VAT and clicks
      // Save before React has committed the state update.
      const rawVat = vatRateInputRef.current?.value;
      const vatRateForSubmit = rawVat !== undefined && rawVat !== '' && !Number.isNaN(parseFloat(rawVat))
        ? parseFloat(rawVat)
        : (formData.vatRate ?? 20);

      const purchaseData = {
        supplier_id: formData.supplierId,
        purchase_num: formData.purchaseNum,
        receipt_number: formData.receiptNumber || null,
        supplier_ref: formData.supplierRef || null,
        purchase_date: formData.purchaseDate || null,
        // Only include delivery_date if "not applicable" is not checked
        ...(deliveryDateNotApplicable ? {} : { delivery_date: formData.deliveryDate || null }),
        vat_rate: vatRateForSubmit,
        notes: formData.notes,
        items: items.map(item => ({
          description: item.description.trim(),
          part_number: item.partNumber || null,
          quantity: parseFloat(item.quantity) || 0,
          unit_price: parseFloat(item.unitPrice) || 0,
          category: item.category || null,
          notes: item.notes || null,
          vat_rate: item.vat_rate ?? null,
          destination_type: item.destinationType || 'expense',
          linked_asset_id: item.linkedAssetId || null,
          linked_stock_item_id: item.linkedStockItemId || null,
        })),
      };

      const closeAfterSave = e?.nativeEvent?.submitter?.getAttribute('data-close-after') === 'true';

      if (isEditMode) {
        await purchasesAPI.update(id, purchaseData);
      } else {
        await purchasesAPI.create(purchaseData);
      }

      if (closeAfterSave || !isEditMode) {
        navigate('/tenant/purchases');
      } else {
        success('Purchase updated');
      }
    } catch (err) {
      // Handle validation errors from express-validator
      const errorData = err.response?.data;
      let errorMessage = 'Failed to save purchase';
      
      if (errorData) {
        // Check for validation errors array (from express-validator)
        if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
          const firstError = errorData.errors[0];
          errorMessage = firstError.msg || firstError.message || `Validation error: ${firstError.param || 'field'}`;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      console.error('Save error:', err);
      if (errorData?.errors) {
        console.error('Validation errors:', errorData.errors);
      }
    } finally {
      setSaving(false);
    }
  };

  const totals = calculateTotals();
  // Live balance due from current form (updates when VAT/items change); amount paid is from server
  const liveBalanceDue = Math.max(0, totals.total - purchaseTotals.amountPaid);

  const getSupplierDisplayName = (supplier) => {
    if (!supplier) return 'Select supplier';
    return supplier.supplier_name || supplier.supplierName || 'Unnamed Supplier';
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      manual: 'Manual',
      stripe: 'Stripe',
      bank_transfer: 'Bank Transfer (BACS)',
      card: 'Credit/Debit Card',
      cash: 'Cash',
      cheque: 'Cheque',
    };
    return labels[method] || method;
  };

  // Determine if purchase is read-only: explicit view mode or cancelled
  const isReadOnly = viewReadOnly || (isEditMode && purchaseStatus === 'cancelled');

  const formatAddress = (obj) => {
    if (!obj) return '';
    const parts = [
      obj.address_line1 || obj.addressLine1,
      obj.address_line2 || obj.addressLine2,
      [obj.city, obj.postcode].filter(Boolean).join(', '),
      obj.county,
    ].filter(Boolean);
    return parts.join(', ') || '';
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const deliveryDateObj = formData.deliveryDate ? new Date(formData.deliveryDate) : null;
  const showOverdue = liveBalanceDue > 0 && deliveryDateObj && deliveryDateObj < today;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading purchase...</p>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
                {isReadOnly ? 'View Purchase' : isEditMode ? 'Edit Purchase' : 'New Purchase'}
              </h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                {isReadOnly ? 'Read-only view' : isEditMode ? 'Update purchase details' : 'Create a new purchase'}
              </p>
            </div>
            <div className="mt-4 flex md:mt-0 md:ml-4 flex-wrap items-center gap-3">
              {viewReadOnly && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate(`/tenant/purchases/${id}`, { replace: true })}
                  className="inline-flex items-center gap-2"
                >
                  <Edit className="h-4 w-4 shrink-0" aria-hidden />
                  Edit Purchase
                </Button>
              )}
              {!isEditMode && aiUploadEnabled && (
                <RotatingGlowButton
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate('/tenant/purchases/ai-upload')}
                  className="inline-flex items-center gap-2 text-orange-500"
                  glowColor="orange"
                  glowSpeed={1}
                  title="Upload purchase invoice using AI"
                >
                  <Sparkles className="h-4 w-4 shrink-0 text-orange-500" aria-hidden />
                  AI Upload
                </RotatingGlowButton>
              )}
              {isEditMode && (aiDraftId || attachments.length > 0) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleViewOriginalDocument}
                  disabled={openingDocument}
                  className="inline-flex items-center gap-2"
                  title={aiSourceFileName || (attachments[0]?.fileName) ? `View ${aiSourceFileName || attachments[0]?.fileName} (opens in new tab)` : 'View receipt (opens in new tab)'}
                >
                  <ExternalLink className="h-4 w-4 shrink-0" aria-hidden />
                  {openingDocument ? 'Opening…' : aiDraftId ? 'View original document' : 'View receipt'}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/tenant/purchases')}
              >
                Cancel
              </Button>
              {isEditMode && purchaseStatus === 'cancelled' && (
                <Badge variant="danger" className="px-4 py-2">
                  Cancelled
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {viewReadOnly ? (
          /* Document-style printable purchase record */
          <div className="max-w-4xl mx-auto">
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-sm overflow-hidden print:shadow-none">
              {/* Header: From | To */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 md:p-8 border-b border-gray-200 dark:border-gray-600">
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">From</p>
                  <p className="text-base font-semibold text-gray-900 dark:text-gray-100">{getSupplierDisplayName(selectedSupplier)}</p>
                  {selectedSupplier?.vat_number && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">VAT {selectedSupplier.vat_number}</p>
                  )}
                  {selectedSupplier && formatAddress(selectedSupplier) && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">{formatAddress(selectedSupplier)}</p>
                  )}
                </div>
                <div className="md:text-right">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">To</p>
                  <p className="text-base font-semibold text-gray-900 dark:text-gray-100">{tenantSettings?.company_name || 'Your Business'}</p>
                  {tenantSettings && formatAddress(tenantSettings) && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">{formatAddress(tenantSettings)}</p>
                  )}
                </div>
              </div>

              {/* Title */}
              <div className="py-6 text-center">
                <h2 className="text-2xl md:text-3xl font-light text-gray-500 dark:text-gray-400 tracking-wide">PURCHASE</h2>
              </div>

              {/* Key info block */}
              <div className="px-6 md:px-8 pb-6 flex flex-wrap gap-x-8 gap-y-2 text-sm">
                <span><span className="text-gray-500 dark:text-gray-400">Receipt No:</span> <span className="font-medium text-gray-900 dark:text-gray-100">{formData.receiptNumber || '-'}</span></span>
                <span><span className="text-gray-500 dark:text-gray-400">Supplier Ref:</span> <span className="font-medium text-gray-900 dark:text-gray-100">{formData.supplierRef || '-'}</span></span>
                <span><span className="text-gray-500 dark:text-gray-400">Receipt Date:</span> <span className="font-medium text-gray-900 dark:text-gray-100">{formData.purchaseDate ? new Date(formData.purchaseDate).toLocaleDateString('en-GB') : '-'}</span></span>
                <span><span className="text-gray-500 dark:text-gray-400">Delivery Date:</span> <span className={`font-medium ${showOverdue ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}>{formData.deliveryDate ? new Date(formData.deliveryDate).toLocaleDateString('en-GB') : '-'}</span></span>
              </div>

              {/* Line items table */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-700/50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Description</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Sub Total</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">VAT Rate</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">VAT</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => {
                      const qty = parseFloat(item.quantity) || 0;
                      const unitPrice = parseFloat(item.unitPrice) || 0;
                      const rate = getEffectiveVatRate(item);
                      const { netPence, vatPence, totalPence } = calculateLinePence(qty, unitPrice, rate);
                      return (
                        <tr key={item.id || index} className="border-t border-gray-200 dark:border-gray-600">
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{getCategoryName(item.category) || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{item.description || '-'}</td>
                          <td className="px-4 py-3 text-sm text-right tabular-nums text-gray-900 dark:text-gray-100">{formatCurrency(fromPence(netPence))}</td>
                          <td className="px-4 py-3 text-sm text-right tabular-nums text-gray-900 dark:text-gray-100">{rate.toFixed(2)}%</td>
                          <td className="px-4 py-3 text-sm text-right tabular-nums text-gray-900 dark:text-gray-100">{formatCurrency(fromPence(vatPence))}</td>
                          <td className="px-4 py-3 text-sm text-right tabular-nums font-medium text-gray-900 dark:text-gray-100">{formatCurrency(fromPence(totalPence))}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* OVERDUE badge */}
              {showOverdue && (
                <div className="px-6 md:px-8 pt-4">
                  <span className="inline-block px-4 py-2 text-sm font-bold text-white bg-red-600 rounded">OVERDUE</span>
                </div>
              )}

              {/* Totals */}
              <div className="px-6 md:px-8 py-6 border-t border-gray-200 dark:border-gray-600">
                <div className="max-w-xs ml-auto space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Sub Total</span>
                    <span className="tabular-nums text-gray-900 dark:text-gray-100">{formatCurrency(totals.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">VAT</span>
                    <span className="tabular-nums text-gray-900 dark:text-gray-100">{formatCurrency(totals.vatAmount)}</span>
                  </div>
                  <div className="flex justify-between text-base font-semibold pt-2 border-t border-gray-200 dark:border-gray-600">
                    <span className="text-gray-900 dark:text-gray-100">Gross Total</span>
                    <span className="tabular-nums text-gray-900 dark:text-gray-100">{formatCurrency(totals.total)}</span>
                  </div>
                  <div className="flex justify-between text-sm pt-2">
                    <span className="text-gray-600 dark:text-gray-400">Paid to Date</span>
                    <span className="tabular-nums text-green-600 dark:text-green-400">{formatCurrency(purchaseTotals.amountPaid)}</span>
                  </div>
                  <div className="flex justify-between text-base font-bold pt-1 bg-gray-50 dark:bg-gray-700/50 -mx-2 px-2 py-2 rounded">
                    <span className="text-gray-900 dark:text-gray-100">Balance (GBP)</span>
                    <span className={`tabular-nums ${liveBalanceDue > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>{formatCurrency(liveBalanceDue)}</span>
                  </div>
                </div>
              </div>

              {/* Collapsible: File Attachments */}
              <div className="border-t border-gray-200 dark:border-gray-600">
                <button
                  type="button"
                  onClick={() => setDocExpandAttachments((p) => !p)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left text-sm font-medium text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <Paperclip className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    File Attachments
                  </span>
                  {docExpandAttachments ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
                {docExpandAttachments && (
                  <div className="px-6 pb-4 text-sm text-gray-600 dark:text-gray-300">
                    {attachments.length === 0 ? (
                      <p>No attachments</p>
                    ) : (
                      <div className="space-y-2">
                        {attachments.map((att) => (
                          <div key={att.id} className="flex items-center justify-between py-2">
                            <span>{att.fileName}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewAttachment(att.id)}
                              disabled={openingDocument}
                              className="text-[var(--ds-accent-primary)]"
                            >
                              View
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Collapsible: My Notes */}
              <div className="border-t border-gray-200 dark:border-gray-600">
                <button
                  type="button"
                  onClick={() => setDocExpandNotes((p) => !p)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left text-sm font-medium text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    My Notes
                  </span>
                  {docExpandNotes ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
                {docExpandNotes && (
                  <div className="px-6 pb-4 text-sm text-gray-600 dark:text-gray-300">
                    {formData.notes ? (
                      <p className="whitespace-pre-wrap">{formData.notes}</p>
                    ) : (
                      <p className="text-gray-400 dark:text-gray-500">You have not yet entered any notes</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 rounded-[var(--ds-radius-md)] p-4">
              <p className="text-xs text-[var(--ds-status-danger)]">{error}</p>
            </div>
          )}

          {/* Basic Information */}
          <Card>
            <CardContent className="p-6">
            <h2 className="text-h2 font-medium text-[var(--ds-text-primary)] mb-4">Purchase Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Supplier Selection */}
              <div className="md:col-span-2">
                <FormLabel required>
                  Supplier
                </FormLabel>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    onClick={() => setShowSupplierPicker(true)}
                    disabled={isReadOnly}
                    className="flex-1 justify-start"
                  >
                    {getSupplierDisplayName(selectedSupplier)}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    onClick={() => setShowNewSupplierSlideOver(true)}
                    disabled={isReadOnly}
                  >
                    + New Supplier
                  </Button>
                </div>
              </div>

              {/* Purchase Number */}
              <div>
                <FormLabel required>
                  Purchase Number
                </FormLabel>
                <Input
                  type="text"
                  value={formData.purchaseNum}
                  onChange={(e) => setFormData(prev => ({ ...prev, purchaseNum: e.target.value }))}
                  required
                  disabled={isReadOnly}
                  className="h-8"
                />
              </div>

              {/* Receipt Number */}
              <div>
                <FormLabel>
                  Receipt Number
                </FormLabel>
                <Input
                  type="text"
                  value={formData.receiptNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, receiptNumber: e.target.value }))}
                  disabled={isReadOnly}
                  placeholder="From supplier invoice/receipt"
                  className="text-xs"
                />
              </div>

              {/* Supplier Reference */}
              <div>
                <FormLabel>
                  Supplier Reference
                </FormLabel>
                <Input
                  type="text"
                  value={formData.supplierRef}
                  onChange={(e) => setFormData(prev => ({ ...prev, supplierRef: e.target.value }))}
                  disabled={isReadOnly}
                  placeholder="Supplier's order number"
                  className="h-8"
                />
              </div>

              {/* Purchase Date */}
              <div>
                <FormLabel>
                  Purchase Date
                </FormLabel>
                <Input
                  type="date"
                  variant="borderless"
                  value={formData.purchaseDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, purchaseDate: e.target.value }))}
                  disabled={isReadOnly}
                  className="text-xs"
                />
              </div>

              {/* Delivery Date */}
              <div>
                <FormLabel>
                  Delivery Date
                </FormLabel>
                <div className="space-y-2">
                  <Input
                    type="date"
                    variant="borderless"
                    value={formData.deliveryDate}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, deliveryDate: e.target.value }));
                      // If user enters a date, uncheck "not applicable"
                      if (e.target.value) {
                        setDeliveryDateNotApplicable(false);
                      }
                    }}
                    disabled={isReadOnly || deliveryDateNotApplicable}
                    className="h-8"
                  />
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={deliveryDateNotApplicable}
                      onChange={(e) => {
                        setDeliveryDateNotApplicable(e.target.checked);
                        if (e.target.checked) {
                          setFormData(prev => ({ ...prev, deliveryDate: '' }));
                        }
                      }}
                      disabled={isReadOnly}
                      className="h-4 w-4 text-[var(--ds-accent-primary)] focus:ring-[var(--ds-accent-primary)] border-[var(--ds-border-subtle)] rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <span className="text-xs text-[var(--ds-text-muted)]">Not applicable</span>
                  </label>
                </div>
              </div>

              {/* VAT Rate */}
              <div>
                <FormLabel>
                  VAT Rate (%)
                </FormLabel>
                <Input
                  ref={vatRateInputRef}
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.vatRate}
                  onChange={(e) => setFormData(prev => ({ ...prev, vatRate: parseFloat(e.target.value) || 0 }))}
                  disabled={isReadOnly}
                  className="h-8"
                />
              </div>

              {/* Notes */}
              <div className="md:col-span-2">
                <FormLabel>
                  Notes
                </FormLabel>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  disabled={isReadOnly}
                  rows={3}
                  placeholder="Additional notes about this purchase..."
                  className="text-xs"
                />
              </div>
            </div>
            </CardContent>
          </Card>

          {/* Purchase Items */}
          <Card>
            <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-h2 font-medium text-[var(--ds-text-primary)]">Items</h2>
              {!isReadOnly && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowVatCalc(prev => !prev)}
                >
                  {showVatCalc ? 'Hide VAT Calc' : 'VAT Calc'}
                </Button>
              )}
            </div>

            {showVatCalc && !isReadOnly && (
              <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <FormLabel className="text-xs mb-1">
                    Unit price inc VAT
                  </FormLabel>
                  <Input
                    type="number"
                    step="0.01"
                    value={vatCalcValues.unitPriceIncVat}
                    onChange={(e) => handleVatCalcChange(e.target.value)}
                    className="h-8 text-xs"
                  />
                </div>
                <div>
                  <FormLabel className="text-xs mb-1">
                    Ex-VAT
                  </FormLabel>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={vatCalcValues.exVat}
                    readOnly
                    className="h-8 text-xs"
                  />
                </div>
                <div>
                  <FormLabel className="text-xs mb-1">
                    VAT
                  </FormLabel>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={vatCalcValues.vat}
                    readOnly
                    className="h-8 text-xs"
                  />
                </div>
              </div>
            )}

            {items.length === 0 ? (
              <div className="overflow-x-auto">
                <table 
                  className="min-w-full divide-y divide-[var(--ds-border-divider)] purchase-items-table table-fixed"
                  style={{
                    // Set column widths based on read-only state
                    ...(isReadOnly ? {
                      // Read-only mode: redistribute widths (no action/remove columns)
                      '--col-category': '16.666667%',      // 2/12
                      '--col-description': '33.333333%',  // 4/12
                      '--col-partNumber': '16.666667%',    // 2/12
                      '--col-qty': '8.333333%',            // 1/12
                      '--col-unitPrice': '8.333333%',      // 1/12
                      '--col-net': '8.333333%',            // 1/12
                      '--col-vat': '0%',                   // 0/12 (hidden)
                      '--col-total': '8.333333%',          // 1/12
                    } : {
                      // Editable mode: default widths (already set in CSS)
                    })
                  }}
                >
                  <colgroup>
                    {!isReadOnly && <col data-col="action" />}
                    <col data-col="category" />
                    <col data-col="description" />
                    <col data-col="partNumber" />
                    <col data-col="qty" />
                    <col data-col="unitPrice" />
                    <col data-col="net" />
                    <col data-col="vat" />
                    <col data-col="total" />
                    {!isReadOnly && <col data-col="remove" />}
                  </colgroup>
                  <thead className="bg-[var(--ds-surface-panel2)]">
                    <tr>
                      {!isReadOnly && (
                        <th className="px-3 py-2 text-center text-meta font-medium text-[var(--ds-text-muted)] uppercase truncate"></th>
                      )}
                      <th className="px-3 py-2 text-left text-meta font-medium text-[var(--ds-text-muted)] uppercase truncate">Category</th>
                      <th className="px-3 py-2 text-left text-meta font-medium text-[var(--ds-text-muted)] uppercase truncate">Description</th>
                      <th className="px-3 py-2 text-left text-meta font-medium text-[var(--ds-text-muted)] uppercase truncate">Part Number</th>
                      <th className="px-3 py-2 text-left text-meta font-medium text-[var(--ds-text-muted)] uppercase truncate">Qty</th>
                      <th className="px-3 py-2 text-left text-meta font-medium text-[var(--ds-text-muted)] uppercase truncate">Unit Price</th>
                      <th className="px-3 py-2 text-right text-meta font-medium text-[var(--ds-text-muted)] uppercase truncate">NET</th>
                      <th className="px-3 py-2 text-right text-meta font-medium text-[var(--ds-text-muted)] uppercase truncate">VAT</th>
                      <th className="px-3 py-2 text-right text-meta font-medium text-[var(--ds-text-muted)] uppercase truncate">Total</th>
                      {!isReadOnly && (
                        <th className="px-3 py-2 text-right text-meta font-medium text-[var(--ds-text-muted)] uppercase truncate"></th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-[var(--ds-surface-panel)] divide-y divide-[var(--ds-border-divider)]">
                    <tr className="hover:bg-[var(--ds-control-field-bg)] transition-colors duration-180">
                      {!isReadOnly && (
                        <td className="px-3 py-2 text-center">
                          <button
                            type="button"
                            onClick={() => handleAddItem()}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                            title="Add first item"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </td>
                      )}
                      <td colSpan={isReadOnly ? 8 : 9} className="px-3 py-8 text-center text-[var(--ds-text-muted)]">
                        {isReadOnly ? 'No items' : 'Click + to add your first item'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table 
                  className="min-w-full divide-y divide-[var(--ds-border-divider)] purchase-items-table table-fixed"
                  style={{
                    // Set column widths based on read-only state
                    ...(isReadOnly ? {
                      // Read-only mode: redistribute widths (no action/remove columns)
                      '--col-category': '16.666667%',      // 2/12
                      '--col-description': '33.333333%',  // 4/12
                      '--col-partNumber': '16.666667%',    // 2/12
                      '--col-qty': '8.333333%',            // 1/12
                      '--col-unitPrice': '8.333333%',      // 1/12
                      '--col-net': '8.333333%',            // 1/12
                      '--col-vat': '0%',                   // 0/12 (hidden)
                      '--col-total': '8.333333%',          // 1/12
                    } : {
                      // Editable mode: default widths (already set in CSS)
                    })
                  }}
                >
                  <colgroup>
                    {!isReadOnly && <col data-col="action" />}
                    <col data-col="destination" />
                    <col data-col="category" />
                    <col data-col="description" />
                    <col data-col="partNumber" />
                    <col data-col="qty" />
                    <col data-col="unitPrice" />
                    <col data-col="net" />
                    <col data-col="vat" />
                    <col data-col="total" />
                    {!isReadOnly && <col data-col="remove" />}
                  </colgroup>
                  <thead className="bg-[var(--ds-surface-panel2)]">
                    <tr>
                      {!isReadOnly && (
                        <th className="px-3 py-2 text-center text-meta font-medium text-[var(--ds-text-muted)] uppercase truncate"></th>
                      )}
                      <th className="px-3 py-2 text-left text-meta font-medium text-[var(--ds-text-muted)] uppercase truncate">Route to</th>
                      <th className="px-3 py-2 text-left text-meta font-medium text-[var(--ds-text-muted)] uppercase truncate">Category</th>
                      <th className="px-3 py-2 text-left text-meta font-medium text-[var(--ds-text-muted)] uppercase truncate">Description</th>
                      <th className="px-3 py-2 text-left text-meta font-medium text-[var(--ds-text-muted)] uppercase truncate">Part Number</th>
                      <th className="px-3 py-2 text-left text-meta font-medium text-[var(--ds-text-muted)] uppercase truncate">Qty</th>
                      <th className="px-3 py-2 text-left text-meta font-medium text-[var(--ds-text-muted)] uppercase truncate">Unit Price</th>
                      <th className="px-3 py-2 text-right text-meta font-medium text-[var(--ds-text-muted)] uppercase truncate">NET</th>
                      <th className="px-3 py-2 text-right text-meta font-medium text-[var(--ds-text-muted)] uppercase truncate">VAT</th>
                      <th className="px-3 py-2 text-right text-meta font-medium text-[var(--ds-text-muted)] uppercase truncate">Total</th>
                      {!isReadOnly && (
                        <th className="px-3 py-2 text-right text-meta font-medium text-[var(--ds-text-muted)] uppercase truncate"></th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-[var(--ds-surface-panel)] divide-y divide-[var(--ds-border-divider)]">
                    {items.map((item, index) => (
                      <tr key={item.id || index} className="hover:bg-[var(--ds-control-field-bg)] transition-colors duration-180">
                        {!isReadOnly && (
                          <td className="px-3 py-2 text-center">
                            <button
                              type="button"
                              onClick={() => handleAddItem(index)}
                              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                              title="Add item after this row"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </td>
                        )}
                        <td className="px-3 py-2 overflow-hidden">
                          <select
                            value={item.destinationType || 'expense'}
                            onChange={(e) => handleUpdateItem(index, 'destinationType', e.target.value)}
                            disabled={isReadOnly}
                            className="w-full px-0 py-1 bg-transparent border-none text-[12px] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {PURCHASE_DESTINATION_TYPES.map((d) => (
                              <option key={d.value} value={d.value}>{d.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2 overflow-hidden">
                          <select
                            value={item.category || ''}
                            onChange={(e) => {
                              // Store the code (not the name)
                              const selectedCode = e.target.value;
                              handleUpdateItem(index, 'category', selectedCode);
                            }}
                            disabled={isReadOnly}
                            className="w-full px-0 py-1 bg-transparent border-none text-[12px] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <option value="">Select Category</option>
                            {SORTED_CATEGORIES.map((cat) => (
                              <option key={cat.code} value={cat.code}>
                                {cat.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2 overflow-hidden">
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => handleUpdateItem(index, 'description', e.target.value)}
                            disabled={isReadOnly}
                            className="w-full px-0 py-1 bg-transparent border-none text-[12px] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 placeholder:text-gray-400 dark:placeholder:text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            placeholder="Item description"
                          />
                        </td>
                        <td className="px-3 py-2 overflow-hidden">
                          <input
                            type="text"
                            value={item.partNumber || ''}
                            onChange={(e) => handleUpdateItem(index, 'partNumber', e.target.value)}
                            disabled={isReadOnly}
                            className="w-full px-0 py-1 bg-transparent border-none text-[12px] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 placeholder:text-gray-400 dark:placeholder:text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            placeholder="Part/SKU"
                          />
                        </td>
                        <td className="px-3 py-2 overflow-hidden">
                          <input
                            type="number"
                            step="0.01"
                            value={item.quantity}
                            onChange={(e) => handleUpdateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                            disabled={isReadOnly}
                            className="no-spinner w-full px-0 py-1 bg-transparent border-none text-[12px] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </td>
                        <td className="px-3 py-2 overflow-hidden">
                          <input
                            type="number"
                            step="0.01"
                            value={item.unitPrice}
                            onChange={(e) => handleUpdateItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                            disabled={isReadOnly}
                            className="no-spinner w-full px-0 py-1 bg-transparent border-none text-[12px] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </td>
                        <td className="px-3 py-2 text-right text-[12px] font-medium text-[var(--ds-text-primary)] truncate">
                          {(() => {
                            const quantity = parseFloat(item.quantity) || 0;
                            const unitPrice = parseFloat(item.unitPrice) || 0;
                            const { netPence } = calculateLinePence(quantity, unitPrice, getEffectiveVatRate(item));
                            return formatCurrency(fromPence(netPence));
                          })()}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {isReadOnly ? (
                            <span className="text-[12px] font-medium text-[var(--ds-text-primary)] truncate">
                              {(() => {
                                const quantity = parseFloat(item.quantity) || 0;
                                const unitPrice = parseFloat(item.unitPrice) || 0;
                                const { vatPence } = calculateLinePence(quantity, unitPrice, getEffectiveVatRate(item));
                                return formatCurrency(fromPence(vatPence));
                              })()}
                            </span>
                          ) : (
                            <div className="flex items-center justify-end gap-1">
                              <input
                                type="text"
                                inputMode="decimal"
                                value={(() => {
                                  const quantity = parseFloat(item.quantity) || 0;
                                  const unitPrice = parseFloat(item.unitPrice) || 0;
                                  const { vatPence } = calculateLinePence(quantity, unitPrice, getEffectiveVatRate(item));
                                  return fromPence(vatPence).toFixed(2);
                                })()}
                                onChange={(e) => handleUpdateItem(index, 'vatAmountOverride', e.target.value)}
                                className="w-full min-w-0 px-0 py-1 bg-transparent border-none text-[12px] text-right text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 placeholder:text-gray-400 dark:placeholder:text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              />
                              {item.vat_rate != null && (
                                <button
                                  type="button"
                                  onClick={() => handleUpdateItem(index, 'vat_rate', null)}
                                  className="p-0.5 rounded text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors shrink-0"
                                  title="Use default VAT rate"
                                >
                                  <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right text-[12px] font-medium text-[var(--ds-text-primary)] truncate">
                          {(() => {
                            const quantity = parseFloat(item.quantity) || 0;
                            const unitPrice = parseFloat(item.unitPrice) || 0;
                            const { totalPence } = calculateLinePence(quantity, unitPrice, getEffectiveVatRate(item));
                            return formatCurrency(fromPence(totalPence));
                          })()}
                        </td>
                        {!isReadOnly && (
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => handleRemoveItem(index)}
                              className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                              title="Remove item"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            </CardContent>
          </Card>

          {/* Totals */}
          <Card>
            <CardContent className="p-6">
            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-[var(--ds-text-muted)]">Subtotal:</span>
                  <span className="font-medium text-[var(--ds-text-primary)]">{formatCurrency(totals.subtotal)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-[var(--ds-text-muted)]">VAT ({formData.vatRate}%):</span>
                  <span className="font-medium text-[var(--ds-text-primary)]">{formatCurrency(totals.vatAmount)}</span>
                </div>
                <div className="flex justify-between text-h2 font-bold pt-2 border-t border-[var(--ds-border-divider)]">
                  <span className="text-[var(--ds-text-primary)]">Total:</span>
                  <span className="text-[var(--ds-text-primary)]">{formatCurrency(totals.total)}</span>
                </div>
                {isEditMode && (
                  <>
                    <div className="flex justify-between text-xs pt-2 border-t border-[var(--ds-border-divider)]">
                      <span className="text-[var(--ds-text-muted)]">Amount Paid:</span>
                      <span className="font-medium text-[var(--ds-status-success)]">{formatCurrency(purchaseTotals.amountPaid)}</span>
              </div>
                    <div className="flex justify-between text-xs font-semibold">
                      <span className="text-h2 text-white">Balance Due:</span>
                      <span className={`text-h2 font-bold ${liveBalanceDue > 0 ? 'text-[var(--ds-status-danger)]' : 'text-[var(--ds-status-success)]'}`}>
                        {formatCurrency(liveBalanceDue)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
            </CardContent>
          </Card>

          {/* Payment History - Only show in edit mode */}
          {isEditMode && (
            <Card>
              <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-h2 font-medium text-[var(--ds-text-primary)]">Payment History</h2>
                {!isReadOnly && liveBalanceDue > 0 && (
                  <Button
                    variant="primary"
                    size="sm"
                    type="button"
                    onClick={() => setShowPaymentForm(true)}
                  >
                    + Record Payment
                  </Button>
                )}
              </div>

              {payments.length === 0 ? (
                <div className="text-center py-8 text-[var(--ds-text-muted)]">
                  <p>No payments recorded yet</p>
                  {!isReadOnly && liveBalanceDue > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      type="button"
                      onClick={() => setShowPaymentForm(true)}
                    >
                      Record your first payment
                    </Button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-[var(--ds-border-divider)]">
                    <thead className="bg-[var(--ds-surface-panel2)]">
                      <tr>
                        <th className="px-4 py-3 text-left text-micro font-medium text-[var(--ds-text-muted)] uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-micro font-medium text-[var(--ds-text-muted)] uppercase">Amount</th>
                        <th className="px-4 py-3 text-left text-micro font-medium text-[var(--ds-text-muted)] uppercase">Method</th>
                        <th className="px-4 py-3 text-left text-micro font-medium text-[var(--ds-text-muted)] uppercase">Bank Account</th>
                        <th className="px-4 py-3 text-left text-micro font-medium text-[var(--ds-text-muted)] uppercase">Reference</th>
                        {!isReadOnly && (
                          <th className="px-4 py-3 text-right text-micro font-medium text-[var(--ds-text-muted)] uppercase">Actions</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-[var(--ds-surface-panel)] divide-y divide-[var(--ds-border-divider)]">
                      {payments.map((payment) => (
                        <tr key={payment.id} className="hover:bg-[var(--ds-surface-panel-hover)]">
                          <td className="px-4 py-3 text-xs text-[var(--ds-text-primary)]">
                            {new Date(payment.payment_date).toLocaleDateString('en-GB')}
                          </td>
                          <td className="px-4 py-3 text-xs font-medium text-[var(--ds-text-primary)]">
                            {formatCurrency(payment.amount)}
                          </td>
                          <td className="px-4 py-3 text-xs text-[var(--ds-text-primary)]">
                            {getPaymentMethodLabel(payment.payment_method)}
                          </td>
                          <td className="px-4 py-3 text-xs text-[var(--ds-text-primary)]">
                            {payment.bank_account_name || '-'}
                          </td>
                          <td className="px-4 py-3 text-xs text-[var(--ds-text-primary)]">
                            {payment.reference || '-'}
                          </td>
                          {!isReadOnly && (
                            <td className="px-4 py-3 text-right text-xs">
                              <Button
                                variant="outline"
                                size="sm"
                                type="button"
                                onClick={async () => {
                                  if (confirm('Are you sure you want to delete this payment?')) {
                                    try {
                                      await purchasePaymentsAPI.delete(payment.id);
                                      handlePaymentRecorded();
                                    } catch (err) {
                                      alert(err.message || 'Failed to delete payment');
                                    }
                                  }
                                }}
                                className="text-[var(--ds-status-danger)] hover:text-[var(--ds-status-danger)]"
                              >
                                Delete
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              </CardContent>
            </Card>
          )}

          {/* Attachments (Receipts) - Only show in edit mode */}
          {isEditMode && (
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-h2 font-medium text-[var(--ds-text-primary)]">Receipts & Documents</h2>
                  {!isReadOnly && (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*"
                        onChange={handleUploadAttachment}
                        className="hidden"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingAttachment}
                        className="inline-flex items-center gap-2"
                      >
                        <Paperclip className="h-4 w-4 shrink-0" aria-hidden />
                        {uploadingAttachment ? 'Uploading…' : 'Upload receipt'}
                      </Button>
                    </>
                  )}
                </div>
                <p className="text-xs text-[var(--ds-text-muted)] mb-4">
                  Store a digital copy of your purchase receipt (PDF, DOC, or image). Useful when you haven&apos;t used the AI upload.
                </p>
                {attachments.length === 0 ? (
                  <div className="text-center py-8 border border-dashed border-[var(--ds-border-subtle)] rounded-lg bg-[var(--ds-surface-panel2)]">
                    <Paperclip className="h-10 w-10 mx-auto text-[var(--ds-text-muted)] mb-2" aria-hidden />
                    <p className="text-sm text-[var(--ds-text-muted)]">No receipts attached</p>
                    {!isReadOnly && (
                      <Button
                        variant="outline"
                        size="sm"
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="mt-2"
                      >
                        Upload receipt
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {attachments.map((att) => (
                      <div
                        key={att.id}
                        className="flex items-center justify-between py-2 px-3 rounded-md bg-[var(--ds-surface-panel2)] border border-[var(--ds-border-subtle)]"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <Paperclip className="h-4 w-4 shrink-0 text-[var(--ds-text-muted)]" aria-hidden />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-[var(--ds-text-primary)] truncate">{att.fileName}</p>
                            <p className="text-xs text-[var(--ds-text-muted)]">
                              {att.fileSize ? `${(att.fileSize / 1024).toFixed(1)} KB` : ''}
                              {att.isPrimary && (
                                <span className="ml-2 text-[var(--ds-accent-primary)]">Primary</span>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            type="button"
                            onClick={() => handleViewAttachment(att.id)}
                            disabled={openingDocument}
                            className="p-1"
                            title="View / Download"
                          >
                            <ExternalLink className="h-4 w-4" aria-hidden />
                          </Button>
                          {!isReadOnly && (
                            <Button
                              variant="ghost"
                              size="sm"
                              type="button"
                              onClick={() => handleDeleteAttachment(att.id)}
                              className="p-1 text-[var(--ds-status-danger)] hover:text-[var(--ds-status-danger)]"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" aria-hidden />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Form Actions */}
          {!isReadOnly && (
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => navigate('/tenant/purchases')}
              >
                Cancel
              </Button>
              {isEditMode && (
                <Button
                  variant="primary"
                  size="sm"
                  type="submit"
                  disabled={saving}
                  data-close-after="true"
                >
                  {saving ? 'Saving...' : 'Update & Close'}
                </Button>
              )}
              <Button
                variant="primary"
                size="sm"
                type="submit"
                disabled={saving}
              >
                {saving ? 'Saving...' : isEditMode ? 'Update' : 'Create Purchase'}
              </Button>
            </div>
          )}
        </form>
        )}
      </div>

      {/* Supplier Picker Modal */}
      {showSupplierPicker && (
        <SupplierPicker
          onSelect={handleSupplierSelect}
          onClose={() => setShowSupplierPicker(false)}
        />
      )}

      {/* New Supplier Slide-Over */}
      {showNewSupplierSlideOver && (
        <SlideOver
          open={showNewSupplierSlideOver}
          onOpenChange={setShowNewSupplierSlideOver}
          title="New Supplier"
          width="w-full max-w-lg"
        >
          <NewSupplierForm
            onSuccess={handleNewSupplierCreated}
            onCancel={() => setShowNewSupplierSlideOver(false)}
          />
        </SlideOver>
      )}

      {/* Payment Form Slide-Over */}
      {showPaymentForm && isEditMode && (
        <SlideOver
          open={showPaymentForm}
          onOpenChange={setShowPaymentForm}
          title="Record Payment"
          width="w-full max-w-lg"
        >
          <PurchasePaymentForm
            purchaseId={id}
            balanceDue={liveBalanceDue}
            onSuccess={handlePaymentRecorded}
            onCancel={() => setShowPaymentForm(false)}
          />
        </SlideOver>
      )}

      {/* Toast Notifications */}
      <ToastContainer />
    </Layout>
  );
};

// Simplified Supplier Form Component for Quick Creation
const NewSupplierForm = ({ onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    supplier_name: '',
    supplier_code: '',
    contact_name: '',
    email: '',
    phone: '',
    address_line1: '',
    city: '',
    postcode: '',
    country: 'GB',
    payment_terms: '',
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.supplier_name.trim()) {
      setError('Supplier name is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await suppliersAPI.create(formData);
      // The API returns { message, supplier }
      const newSupplier = response.supplier || response;
      onSuccess(newSupplier);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Failed to create supplier');
      console.error('Create supplier error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
          <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Supplier Name - Required */}
      <div>
        <FormLabel required>
          Supplier Name
        </FormLabel>
        <Input
          type="text"
          name="supplier_name"
          value={formData.supplier_name}
          onChange={handleChange}
          required
          placeholder="ABC Electrical Supplies Ltd"
          autoFocus
          className="h-8"
        />
      </div>

      {/* Supplier Code */}
      <div>
        <FormLabel>
          Supplier Code
        </FormLabel>
        <Input
          type="text"
          name="supplier_code"
          value={formData.supplier_code}
          onChange={handleChange}
          placeholder="ABC-001"
          className="h-8"
        />
      </div>

      {/* Contact Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <FormLabel>
            Contact Name
          </FormLabel>
          <Input
            type="text"
            name="contact_name"
            value={formData.contact_name}
            onChange={handleChange}
            placeholder="John Smith"
            className="h-8"
          />
        </div>
        <div>
          <FormLabel>
            Email
          </FormLabel>
          <Input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="supplier@example.com"
            className="h-8"
          />
        </div>
      </div>

      {/* Phone */}
      <div>
        <FormLabel>
          Phone
        </FormLabel>
        <Input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          placeholder="01234 567890"
          className="h-8"
        />
      </div>

      {/* Address */}
      <div className="space-y-4">
        <h3 className="text-xs font-medium text-gray-900 dark:text-gray-100">Address (Optional)</h3>
        <div>
          <FormLabel>
            Address Line 1
          </FormLabel>
          <Input
            type="text"
            name="address_line1"
            value={formData.address_line1}
            onChange={handleChange}
            placeholder="123 Main Street"
            className="h-8"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FormLabel>
              City
            </FormLabel>
            <Input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="Manchester"
              className="h-8"
            />
          </div>
          <div>
            <FormLabel>
              Postcode
            </FormLabel>
            <Input
              type="text"
              name="postcode"
              value={formData.postcode}
              onChange={handleChange}
              placeholder="M1 1AA"
              className="h-8"
            />
          </div>
        </div>
      </div>

      {/* Payment Terms */}
      <div>
        <FormLabel>
          Payment Terms
        </FormLabel>
        <Input
          type="text"
          name="payment_terms"
          value={formData.payment_terms}
          onChange={handleChange}
          placeholder="Net 30"
          className="h-8"
        />
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create Supplier'}
        </Button>
      </div>
    </form>
  );
};

export default PurchaseEditor;

